import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Box } from '@tanium/react-box';
import { useTranslation } from 'react-i18next';
import Page from 'components/base_page/Page';
import Spinner from 'components/common/spinner/Spinner';
import { ActionStatus } from 'store/ActionState';
import AwsRoleTargetAccessSlice from 'components/pages/console/store/AwsRoleTargetAccessSlice';
import BastionTargetAccessSlice from 'components/pages/console/store/BastionTargetAccessSlice';
import HostTargetAccessSlice from 'components/pages/console/store/HostTargetAccessSlice';
import { buildTargetAccessIDtoNameLookup } from 'utils/Helper';
import AwsRoleTargetAccessSelectors from 'components/pages/console/store/AwsRoleTargetAccessSelectors';
import BastionTargetAccessSelectors from 'components/pages/console/store/BastionTargetAccessSelectors';
import HostTargetAccessSelectors from 'components/pages/console/store/HostTargetAccessSelectors';
import TbacDetails from 'components/common/accessInfo/TbacDisplay';
import NotificationInfoDisplay from 'components/common/notificationInfo/NotificationInfoDisplay';
import AwsRoleTargetAccessList from 'components/common/awsRoleTargetAccess/awsRoleTargetAccessList';
import { PermissionAction, PermissionResource, hasPermission } from 'gql/Permission';
import HostTargetAccessList from 'components/common/hostTargetAccess/hostTargetAccessList';
import BastionTargetAccessList from 'components/common/bastionTargetAccess/bastionTargetAccessList';
import UserSelectors from 'components/app/store/UserSelectors';
import CustomerCloudDetails from 'components/pages/customer/customerPartials/CustomerCloudDetails';
import K8sRoleAccessList from 'components/common/K8sRoleTargetAccess/K8sRoleAccessList';
import { CREATE_REQUEST_ROUTE } from 'Route';
import {
  TARGET_ID,
  TARGET_TYPE,
} from 'components/pages/request/requestPartials/createRequestFieldsPartials/Environments';
import { RequestTargetType } from 'gql/Request';
import { Button } from '@tanium/react-button';
import RequestListPanel from 'components/common/requestListPanel/RequestListPanel';
import CustomerSlice from '../console/store/CustomerSlice';
import CustomerSelectors from '../console/store/CustomerSelectors';
import CustomerDetails from './customerPartials/CustomerDetails';

// This page manages a single customer
const CustomerPage: React.VFC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { customerId } = useParams<{ customerId: string }>();
  const customer = useSelector(CustomerSelectors.selectCustomer);
  const fetchState = useSelector(CustomerSelectors.selectFetchCustomerState);
  const user = useSelector(UserSelectors.selectUser);
  const awsRoleAccess = useSelector(AwsRoleTargetAccessSelectors.selectAwsRoleTargetAccesses);
  const bastionAccess = useSelector(BastionTargetAccessSelectors.selectBastionTargetAccesses);
  const hostAccess = useSelector(HostTargetAccessSelectors.selectHostTargetAccesses);
  const [targetAccessLookup, setTargetAccessLookup] = useState({});

  useEffect(() => {
    dispatch(CustomerSlice.actions.fetchCustomer({ id: customerId }));

    return () => {
      dispatch(CustomerSlice.actions.clearCustomerState());
    };
  }, [dispatch, customerId]);

  useEffect(() => {
    dispatch(
      AwsRoleTargetAccessSlice.actions.listAwsRoleTargetAccessForTargetOrGroup({
        includeAllTargetGroups: true,
        targetOrTargetGroupID: customerId,
      })
    );
    dispatch(
      BastionTargetAccessSlice.actions.listBastionTargetAccessForTargetOrGroup({
        includeAllTargetGroups: true,
        targetOrTargetGroupID: customerId,
      })
    );
    dispatch(
      HostTargetAccessSlice.actions.listHostTargetAccessForTargetOrGroup({
        includeAllTargetGroups: true,
        targetOrTargetGroupID: customerId,
      })
    );

    return () => {
      dispatch(AwsRoleTargetAccessSlice.actions.clearState());
      dispatch(BastionTargetAccessSlice.actions.clearState());
      dispatch(HostTargetAccessSlice.actions.clearState());
    };
  }, [dispatch, customerId]);

  useEffect(() => {
    const lookup = buildTargetAccessIDtoNameLookup(awsRoleAccess, [], bastionAccess, hostAccess, []);
    setTargetAccessLookup(lookup);
  }, [awsRoleAccess, bastionAccess, hostAccess]);

  const buttons: JSX.Element[] = [];

  const canCreateRequest = hasPermission(user?.permissions, PermissionAction.WRITE, PermissionResource.REQUEST);
  if (canCreateRequest) {
    buttons.push(
      <Link
        to={`${CREATE_REQUEST_ROUTE.url}?${TARGET_TYPE}=${RequestTargetType.CustomerEnvironment}&${TARGET_ID}=${customerId}`}
      >
        <Button variant="secondary" data-testid={'request-access'}>
          {t('REQUEST_ACCESS')}
        </Button>
      </Link>
    );
  }

  return (
    <Page
      title={customerId}
      breadcrumbs={[{ href: '/?tab=customers', label: t('CUSTOMERS') }]}
      testId="customer-page"
      buttons={buttons}
    >
      {fetchState === ActionStatus.PROCESSING ? (
        <Spinner />
      ) : (
        <Box mb={2}>
          <Box pb={1}>
            <CustomerDetails customer={customer} />
          </Box>
          <Box pb={1}>
            <CustomerCloudDetails customer={customer} />
          </Box>
          <Box pb={1}>
            <TbacDetails baseTarget={customer} targetAccessLookup={targetAccessLookup} />
          </Box>
          <Box pb={1}>
            <NotificationInfoDisplay
              approvalSlackChannel={customer?.approvalSlackChannel}
              notifyAutoApproval={customer?.notifyAutoApproval}
              enablePaging={customer?.enablePaging}
            />
          </Box>
          <Box pb={1}>
            <RequestListPanel targetId={customerId} />
          </Box>
          <Box pb={1}>
            <AwsRoleTargetAccessList
              targetOrGroupID={customerId}
              allowEditing={hasPermission(
                user?.permissions,
                PermissionAction.WRITE,
                PermissionResource.AWS_ROLE_TARGET_ACCESS
              )}
            />
          </Box>
          <Box pb={1}>
            <HostTargetAccessList
              targetOrGroupID={customerId}
              allowEditing={hasPermission(
                user?.permissions,
                PermissionAction.WRITE,
                PermissionResource.HOST_TARGET_ACCESS
              )}
            />
          </Box>
          <Box pb={1}>
            <BastionTargetAccessList
              targetOrGroupID={customerId}
              allowEditing={hasPermission(
                user?.permissions,
                PermissionAction.WRITE,
                PermissionResource.BASTION_TARGET_ACCESS
              )}
            />
          </Box>
          <Box pb={1}>
            <K8sRoleAccessList
              targetOrGroupID={customerId}
              canEdit={hasPermission(
                user?.permissions,
                PermissionAction.WRITE,
                PermissionResource.K8S_ROLE_TARGET_ACCESS
              )}
              includeAllTargetGroups={true}
            />
          </Box>
        </Box>
      )}
    </Page>
  );
};

export default CustomerPage;
